import { supabase } from "./supabase";

const INCEPTBENCH_VERSIONS_URL = "https://api.inceptbench.com/versions";
const INCEPTBENCH_CURRICULUMS_URL = "https://api.inceptbench.com/curriculums";

/**
 * Fetch available InceptBench API versions
 * Returns { current, supported_versions, default } from the API
 * @returns {Promise<{data: Object, error: Object}>}
 */
export const fetchInceptBenchVersions = async () => {
  try {
    const response = await fetch(INCEPTBENCH_VERSIONS_URL, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch versions: ${response.status}`);
    }

    const data = await response.json();

    // Build array of all available versions from supported_versions
    const allVersions = [];

    if (data.supported_versions && Array.isArray(data.supported_versions)) {
      data.supported_versions.forEach((v, index) => {
        allVersions.push({
          version: v,
          isLatest: index === 0, // First version is the latest
          isDefault: data.default === v,
        });
      });
    }

    return {
      data: {
        versions: allVersions,
        current: data.current,
        default: data.default,
      },
      error: null,
    };
  } catch (error) {
    console.error("Error fetching InceptBench versions:", error);
    return { data: null, error };
  }
};

/**
 * Fetch available curriculum versions from the InceptBench API
 * Returns curriculum version list with dates for dropdown display
 * @returns {Promise<{data: Object, error: Object}>}
 */
export const fetchCurriculumVersions = async () => {
  try {
    const response = await fetch(INCEPTBENCH_CURRICULUMS_URL, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(
        `Failed to fetch curriculum versions: ${response.status}`
      );
    }

    const data = await response.json();

    // Build array of all curriculum versions (from the default curriculum)
    const allVersions = [];
    const defaultCurriculum = data.default_curriculum;

    if (data.curricula && typeof data.curricula === "object") {
      // Use the default curriculum's versions, or fall back to first available
      const curriculumKey = defaultCurriculum || Object.keys(data.curricula)[0];
      const curriculum = data.curricula[curriculumKey];

      if (
        curriculum &&
        curriculum.versions &&
        Array.isArray(curriculum.versions)
      ) {
        curriculum.versions.forEach((v, index) => {
          allVersions.push({
            version: v.version,
            versionDate: v.version_date,
            isLatest: v.version === curriculum.latest,
          });
        });
      }
    }

    return {
      data: {
        versions: allVersions,
        defaultCurriculum,
      },
      error: null,
    };
  } catch (error) {
    console.error("Error fetching curriculum versions:", error);
    return { data: null, error };
  }
};

/**
 * Fetch app configuration (including maintenance mode)
 * @returns {Promise<{data: Object, error: Object}>}
 */
export const fetchConfig = async () => {
  try {
    const { data, error } = await supabase
      .from("bm_config")
      .select(
        "maintenance_mode, maintenance_message, expected_resume_time, updated_at"
      )
      .eq("id", 1)
      .single();

    if (error) {
      // If table doesn't exist yet, return default values
      if (error.code === "PGRST116" || error.code === "42P01") {
        return {
          data: {
            maintenance_mode: false,
            maintenance_message: null,
            expected_resume_time: null,
          },
          error: null,
        };
      }
      throw error;
    }

    return { data, error: null };
  } catch (error) {
    console.error("Error fetching config:", error);
    // Return safe defaults on error
    return {
      data: {
        maintenance_mode: false,
        maintenance_message: null,
        expected_resume_time: null,
      },
      error,
    };
  }
};

/**
 * Check if maintenance mode is enabled
 * @returns {Promise<{isMaintenanceMode: boolean, message: string, expectedResumeTime: string|null}>}
 */
export const checkMaintenanceMode = async () => {
  const { data } = await fetchConfig();
  return {
    isMaintenanceMode: data?.maintenance_mode || false,
    message: data?.maintenance_message || null,
    expectedResumeTime: data?.expected_resume_time || null,
  };
};
